import React, { useState, useCallback, useRef } from 'react';
import { Upload, Trash2, FolderOpen } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useStorage } from '../../hooks/useStorage';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { FirebaseImage } from '../FirebaseImage';
import { deleteDocument } from '../../lib/firestore-helpers';
import type { GalleryItem, HeroSlide, TireBrand, ServiceImage } from '../../types/firebase';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 10 * 1024 * 1024; // 10 Mo

const FOLDERS = [
  { value: 'gallery', label: 'Galerie' },
  { value: 'services', label: 'Services' },
  { value: 'hero', label: 'Hero' },
  { value: 'brands', label: 'Marques' },
] as const;

type FolderType = (typeof FOLDERS)[number]['value'];

interface AllImage {
  id: string;
  source: string;
  imageUrl: string;
  thumbnailUrl?: string;
  label: string;
}

const MediaTab: React.FC = () => {
  const { uploadFile, uploading, progress } = useStorage();
  const [folder, setFolder] = useState<FolderType>('gallery');
  const [dragOver, setDragOver] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: galleryItems, refetch: refetchGallery } = useFirestoreCollection<GalleryItem>({
    collection: 'gallery',
    realtime: true,
  });
  const { data: heroSlides, refetch: refetchHero } = useFirestoreCollection<HeroSlide>({
    collection: 'hero-slides',
    realtime: true,
  });
  const { data: tireBrands, refetch: refetchBrands } = useFirestoreCollection<TireBrand>({
    collection: 'tire-brands',
    realtime: true,
  });
  const { data: serviceImages, refetch: refetchServices } = useFirestoreCollection<ServiceImage>({
    collection: 'service-images',
    realtime: true,
  });

  // Merge all images for display
  const allImages: AllImage[] = [
    ...galleryItems.map((g) => ({
      id: g.id,
      source: 'gallery',
      imageUrl: g.imageUrl,
      thumbnailUrl: g.thumbnailUrl,
      label: g.title?.fr || g.title?.en || 'Galerie',
    })),
    ...heroSlides.map((h) => ({
      id: h.id,
      source: 'hero-slides',
      imageUrl: h.imageUrl,
      label: `Hero: ${h.key}`,
    })),
    ...tireBrands.map((b) => ({
      id: b.id,
      source: 'tire-brands',
      imageUrl: b.logoUrl,
      label: `Marque: ${b.name}`,
    })),
    ...serviceImages.map((s) => ({
      id: s.id,
      source: 'service-images',
      imageUrl: s.imageUrl,
      label: `Service: ${s.serviceKey}`,
    })),
  ];

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `Type non accepte: ${file.type}. Formats autorises: JPEG, PNG, WebP.`;
    }
    if (file.size > MAX_SIZE) {
      return `Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo). Max: 10 Mo.`;
    }
    return null;
  };

  const handleUpload = useCallback(
    async (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        const err = validateFile(file);
        if (err) {
          toast.error(err);
          continue;
        }

        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${folder}/${timestamp}_${safeName}`;

        try {
          await uploadFile(file, path);
          toast.success(`"${file.name}" uploade avec succes`);
        } catch {
          toast.error(`Erreur upload "${file.name}"`);
        }
      }
    },
    [folder, uploadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [handleUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDeleteImage = async (img: AllImage) => {
    try {
      await deleteDocument(img.source, img.id);
      toast.success('Image supprimee');
      setDeleteConfirm(null);
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Mediatheque</h2>

      {/* Upload zone */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Upload</h3>
          <div className="flex items-center space-x-2">
            <FolderOpen className="w-4 h-4 text-gray-500" />
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value as FolderType)}
              className="bg-gray-100 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {FOLDERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">
            Glisser-deposer des images ici ou cliquer pour selectionner
          </p>
          <p className="text-sm text-gray-400 mt-1">
            JPEG, PNG, WebP -- max 10 Mo
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {uploading && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Upload en cours...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-red-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Image grid */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Toutes les images ({allImages.length})
        </h3>

        {allImages.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucune image</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {allImages.map((img) => (
              <div key={`${img.source}-${img.id}`} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <FirebaseImage
                    src={img.thumbnailUrl || img.imageUrl}
                    alt={img.label}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{img.label}</p>
                <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                  {img.source}
                </span>

                {/* Delete button */}
                <button
                  onClick={() => setDeleteConfirm(img.id)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>

                {/* Confirm delete */}
                {deleteConfirm === img.id && (
                  <div className="absolute inset-0 bg-black/70 rounded-lg flex flex-col items-center justify-center gap-2">
                    <p className="text-white text-xs font-medium">Supprimer ?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDeleteImage(img)}
                        className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700"
                      >
                        Oui
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="bg-gray-600 text-white text-xs px-3 py-1 rounded hover:bg-gray-700"
                      >
                        Non
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaTab;
